import { InputAdornment, TextField } from '@mui/material';
import React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import ReadOnlyField from './readonly-field';

interface NumberFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  isReadOnly: boolean;
  required?: boolean;
  fullWidth?: boolean;
  min?: number;
  max?: number;
  step?: number;
  helperText?: string;
  unitLabel?: string;
  readonly onChange?: (value: number | undefined) => void;
}

/**
 * A reusable number input field component that supports both
 * editable and read-only modes
 */
const NumberField = <T extends FieldValues>({
  name,
  control,
  label,
  isReadOnly,
  required = false,
  fullWidth = true,
  min,
  max,
  step = 1,
  helperText,
  unitLabel,
  onChange,
}: NumberFieldProps<T>): React.ReactElement => {
  if (isReadOnly) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const hasValue = field.value !== undefined && field.value !== '';

          let displayValue = '';
          if (hasValue) {
            if (unitLabel) {
              displayValue = `${field.value} ${unitLabel}`;
            } else {
              displayValue = field.value;
            }
          }

          return <ReadOnlyField label={label} value={displayValue} />;
        }}
      />
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        // Ensure we have a clean conversion between string and number
        const value = field.value === undefined || field.value === '' ? '' : field.value;

        // For integer fields (step === 1), prevent decimal input
        const isIntegerField = step === 1;

        return (
          <TextField
            {...field}
            fullWidth={fullWidth}
            label={label}
            type="number"
            value={value}
            onChange={e => {
              let inputValue = e.target.value;

              // For integer fields, prevent decimal point and trailing decimals
              if (isIntegerField && inputValue !== '') {
                // Remove any decimal point and everything after it
                if (inputValue.includes('.')) {
                  inputValue = inputValue.split('.')[0];
                }
                // Only allow digits and minus sign at the start
                inputValue = inputValue.replace(/[^\d-]/g, '');
                // Ensure minus sign only appears at the start
                if (inputValue.includes('-') && inputValue.indexOf('-') !== 0) {
                  inputValue = inputValue.replace(/-/g, '');
                }
              }

              const val = inputValue === '' ? '' : Number(inputValue);
              field.onChange(val);
              if (onChange) {
                onChange(val === '' ? undefined : val);
              }
            }}
            onBlur={() => {
              // For integer fields, ensure value is always an integer on blur
              if (isIntegerField && field.value !== undefined && field.value !== '') {
                const intValue = Math.floor(Number(field.value));
                if (intValue !== Number(field.value)) {
                  field.onChange(intValue);
                  if (onChange) {
                    onChange(intValue);
                  }
                }
              }
              // Call original onBlur if it exists
              field.onBlur();
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message || helperText}
            required={required}
            inputProps={{
              min,
              max,
              step,
            }}
            InputProps={{
              endAdornment: unitLabel ? (
                <InputAdornment position="end">{unitLabel}</InputAdornment>
              ) : null,
            }}
          />
        );
      }}
    />
  );
};

export default NumberField;
